// Phase 9 R3/R4 — Linked inventory context strip on a parts row.
// Shows bin / available / reserved / consumed + inline Reserve/Release/Consume/Return.
//
// Truth-refresh contract (R4): all four mutations invalidate
//   ['inventory-item', shopId, itemId] and ['parts-lines', shopId, ticketId]
// (plus inventory-items prefix and inventory-transactions). The strip re-renders
// from refetched queries — no optimistic updates on avail/reserved/consumed.
// Backend is the single source of truth.
//
// Read-only mode (R4): when `inventoryEnabled` is false but the line is already
// linked, the strip still renders the values so existing operational truth is
// not hidden. All mutating buttons are suppressed.
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Boxes, Loader2 } from 'lucide-react';
import { useInventoryItemById } from '@/hooks/inventory/useInventoryItemById';
import { useReserveInventoryForPartsLine } from '@/hooks/inventory/useReserveInventoryForPartsLine';
import { useReleaseInventoryReservation } from '@/hooks/inventory/useReleaseInventoryReservation';
import { useConsumeInventoryForPartsLine } from '@/hooks/inventory/useConsumeInventoryForPartsLine';
import { useReturnConsumedToStock } from '@/hooks/inventory/useReturnConsumedToStock';
import { StockPill } from '@/components/inventory/StockPill';
import type { PartsLine } from '@/hooks/parts/usePartsLines';

interface Props {
  line: PartsLine;
  canMutate: boolean;
  /** When false, strip is read-only — values shown, no mutating controls. */
  inventoryEnabled: boolean;
}

export function LinkedInventoryStrip({ line, canMutate, inventoryEnabled }: Props) {
  const { data: item, isLoading } = useInventoryItemById(line.inventory_item_id);
  const reserveMut = useReserveInventoryForPartsLine();
  const releaseMut = useReleaseInventoryReservation();
  const consumeMut = useConsumeInventoryForPartsLine();
  const returnMut = useReturnConsumedToStock();

  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveQty, setReserveQty] = useState<string>(String(line.quantity ?? 1));
  const [consumeOpen, setConsumeOpen] = useState(false);
  const [consumeQty, setConsumeQty] = useState<string>('1');
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnQty, setReturnQty] = useState<string>('1');

  if (!line.inventory_item_id) return null;
  if (isLoading) {
    return (
      <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading stock…
      </div>
    );
  }
  if (!item) return null;

  const avail = item.quantity_available ?? 0;
  const rp = item.reorder_point ?? 0;
  const reserved = Number(line.inventory_reservation_qty ?? 0);
  const consumed = Number(line.inventory_consumed_qty ?? 0);

  // R4 visibility rules.
  const showMutators = canMutate && inventoryEnabled;
  const reserveMax = avail + reserved; // backend re-validates
  const showReserve = showMutators && reserveMax > 0;
  const showRelease = showMutators && reserved > 0;
  const showConsume = showMutators && (reserved > 0 || avail > 0);
  const showReturn = showMutators && consumed > 0;

  const consumeMax = reserved + avail;
  const consumeDefault = reserved > 0
    ? reserved
    : Math.max(Number(line.quantity ?? 1) - consumed, 1);

  const handleReserve = async () => {
    const n = Number(reserveQty);
    if (!Number.isFinite(n) || n <= 0) return;
    await reserveMut.mutateAsync({
      partsLineId: line.id,
      qty: n,
      ticketId: line.ticket_id,
      inventoryItemId: line.inventory_item_id!,
    });
    setReserveOpen(false);
  };

  const handleRelease = async () => {
    await releaseMut.mutateAsync({
      partsLineId: line.id,
      ticketId: line.ticket_id,
      inventoryItemId: line.inventory_item_id!,
    });
  };

  const handleConsume = async () => {
    const n = Number(consumeQty);
    if (!Number.isFinite(n) || n <= 0) return;
    await consumeMut.mutateAsync({
      partsLineId: line.id,
      qty: n,
      ticketId: line.ticket_id,
      inventoryItemId: line.inventory_item_id!,
    });
    setConsumeOpen(false);
  };

  const handleReturn = async () => {
    const n = Number(returnQty);
    if (!Number.isFinite(n) || n <= 0) return;
    await returnMut.mutateAsync({
      partsLineId: line.id,
      qty: n,
      ticketId: line.ticket_id,
      inventoryItemId: line.inventory_item_id!,
    });
    setReturnOpen(false);
  };

  return (
    <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[11px]">
      <Badge variant="secondary" className="h-4 py-0 gap-1">
        <Boxes className="h-2.5 w-2.5" /> Stock
      </Badge>
      <span className="text-muted-foreground tabular-nums">
        {item.bin_location && <>Bin {item.bin_location} · </>}
        Avail {avail}
        {reserved > 0 && <> · Reserved {reserved}</>}
        {consumed > 0 && <> · Consumed {consumed}</>}
      </span>
      <StockPill available={avail} reorderPoint={rp} />

      {showReserve && (
        <Popover open={reserveOpen} onOpenChange={setReserveOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[11px]"
              onClick={() => setReserveQty(String(line.quantity ?? 1))}
            >
              Reserve
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">
                Max reservable: <span className="tabular-nums">{reserveMax}</span>
              </div>
              <Input
                type="number"
                min="0"
                step="1"
                value={reserveQty}
                onChange={(e) => setReserveQty(e.target.value)}
                className="h-7"
              />
              <div className="flex justify-end gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7"
                  onClick={() => setReserveOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-7"
                  onClick={handleReserve}
                  disabled={
                    reserveMut.isPending ||
                    !Number(reserveQty) ||
                    Number(reserveQty) <= 0 ||
                    Number(reserveQty) > reserveMax
                  }
                >
                  Confirm
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {showRelease && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-5 px-1.5 text-[11px] text-destructive"
          onClick={handleRelease}
          disabled={releaseMut.isPending}
        >
          Release
        </Button>
      )}

      {showConsume && (
        <Popover
          open={consumeOpen}
          onOpenChange={(o) => {
            setConsumeOpen(o);
            if (o) setConsumeQty(String(consumeDefault));
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[11px]"
            >
              Consume
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">
                Max consumable: <span className="tabular-nums">{consumeMax}</span>
                {reserved > 0 && (
                  <span className="block text-[10px]">
                    (consumes reserved first)
                  </span>
                )}
              </div>
              <Input
                type="number"
                min="0"
                step="1"
                value={consumeQty}
                onChange={(e) => setConsumeQty(e.target.value)}
                className="h-7"
              />
              <div className="flex justify-end gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7"
                  onClick={() => setConsumeOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-7"
                  onClick={handleConsume}
                  disabled={
                    consumeMut.isPending ||
                    !Number(consumeQty) ||
                    Number(consumeQty) <= 0 ||
                    Number(consumeQty) > consumeMax
                  }
                >
                  Confirm
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {showReturn && (
        <Popover
          open={returnOpen}
          onOpenChange={(o) => {
            setReturnOpen(o);
            if (o) setReturnQty(String(consumed));
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-5 px-1.5 text-[11px]"
            >
              Return
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start">
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">
                Max returnable: <span className="tabular-nums">{consumed}</span>
              </div>
              <Input
                type="number"
                min="0"
                step="1"
                value={returnQty}
                onChange={(e) => setReturnQty(e.target.value)}
                className="h-7"
              />
              <div className="flex justify-end gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7"
                  onClick={() => setReturnOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-7"
                  onClick={handleReturn}
                  disabled={
                    returnMut.isPending ||
                    !Number(returnQty) ||
                    Number(returnQty) <= 0 ||
                    Number(returnQty) > consumed
                  }
                >
                  Confirm
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
